import React from 'react';

const CHUNK_RECOVERY_MARKER_KEY = '__oceanmind_chunk_recovery_marker__';
const CHUNK_RECOVERY_COOLDOWN_MS = 60 * 1000;
const CHUNK_RECOVERY_CLEAR_DELAY_MS = 5000;

const isOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

const isLikelyChunkLoadError = (error) => {
  const message = error?.message || '';
  const name = error?.name || '';
  const combined = `${name} ${message}`.toLowerCase();

  const hasChunkSignal =
    combined.includes('chunk')
    || combined.includes('chunkloaderror')
    || combined.includes('loading css chunk');
  const hasDynamicImportSignal =
    combined.includes('failed to fetch dynamically imported module')
    || combined.includes('importing a module script failed')
    || combined.includes('dynamically imported module');
  const hasFetchFailureSignal =
    combined.includes('failed to fetch')
    || combined.includes('load failed');
  const hasModuleSignal = combined.includes('module');

  return hasChunkSignal || hasDynamicImportSignal || (hasFetchFailureSignal && hasModuleSignal);
};

const readChunkRecoveryMarker = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(CHUNK_RECOVERY_MARKER_KEY);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue || typeof parsedValue !== 'object') {
      return null;
    }

    return {
      path: typeof parsedValue.path === 'string' ? parsedValue.path : '',
      timestamp: Number(parsedValue.timestamp) || 0
    };
  } catch {
    return null;
  }
};

const clearChunkRecoveryMarker = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(CHUNK_RECOVERY_MARKER_KEY);
  } catch {
  }
};

const markChunkRecoveryAttempt = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(
      CHUNK_RECOVERY_MARKER_KEY,
      JSON.stringify({
        path: window.location.pathname || '/',
        timestamp: Date.now()
      })
    );
  } catch {
  }
};

const hasRecentChunkRecoveryAttempt = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const marker = readChunkRecoveryMarker();
  if (!marker) {
    return false;
  }

  if (Date.now() - marker.timestamp > CHUNK_RECOVERY_COOLDOWN_MS) {
    clearChunkRecoveryMarker();
    return false;
  }

  return marker.path === (window.location.pathname || '/');
};

class RouteLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      isAutoRecovering: false,
      isOffline: isOffline()
    };
    this.clearRecoveryTimer = null;
    this.autoRecoverTimer = null;
  }

  componentDidMount() {
    this.scheduleRecoveryMarkerCleanup();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  componentDidUpdate(prevProps) {
    this.scheduleRecoveryMarkerCleanup();

    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.resetBoundaryError({ clearRecoveryMarker: true });
    }
  }

  componentWillUnmount() {
    if (this.clearRecoveryTimer && typeof window !== 'undefined') {
      window.clearTimeout(this.clearRecoveryTimer);
      this.clearRecoveryTimer = null;
    }

    this.clearAutoRecoverTimer();

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[Route] Failed to load route bundle', error, errorInfo);

    if (isLikelyChunkLoadError(error)) {
      if (isOffline()) {
        this.setState({ isOffline: true, isAutoRecovering: false });
        return;
      }

      this.tryAutoRecover();
    }
  }

  clearAutoRecoverTimer = () => {
    if (this.autoRecoverTimer && typeof window !== 'undefined') {
      window.clearTimeout(this.autoRecoverTimer);
      this.autoRecoverTimer = null;
    }
  };

  resetBoundaryError = ({ clearRecoveryMarker = false } = {}) => {
    this.clearAutoRecoverTimer();

    if (clearRecoveryMarker) {
      clearChunkRecoveryMarker();
    }

    this.setState({
      error: null,
      isAutoRecovering: false,
      isOffline: isOffline()
    });
  };

  handleRetryRender = () => {
    this.resetBoundaryError({ clearRecoveryMarker: true });
  };

  handleOnline = () => {
    this.setState({ isOffline: false });

    if (this.state.error && isLikelyChunkLoadError(this.state.error)) {
      this.tryAutoRecover({ ignoreCooldown: true });
    }
  };

  handleOffline = () => {
    this.clearAutoRecoverTimer();
    this.setState({ isOffline: true, isAutoRecovering: false });
  };

  scheduleRecoveryMarkerCleanup = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.clearRecoveryTimer) {
      window.clearTimeout(this.clearRecoveryTimer);
    }

    this.clearRecoveryTimer = window.setTimeout(() => {
      this.clearRecoveryTimer = null;
      if (!this.state.error) {
        clearChunkRecoveryMarker();
      }
    }, CHUNK_RECOVERY_CLEAR_DELAY_MS);
  };

  tryAutoRecover = (options = {}) => {
    const ignoreCooldown = options.ignoreCooldown === true;

    if (typeof window === 'undefined') {
      return false;
    }

    if (isOffline()) {
      this.setState({ isOffline: true, isAutoRecovering: false });
      return false;
    }

    if (!ignoreCooldown && hasRecentChunkRecoveryAttempt()) {
      return false;
    }

    markChunkRecoveryAttempt();
    this.setState({ isAutoRecovering: true, isOffline: false });

    this.clearAutoRecoverTimer();

    this.autoRecoverTimer = window.setTimeout(() => {
      this.autoRecoverTimer = null;
      this.handleReload({ preserveRecoveryMarker: true });
    }, 120);

    return true;
  };

  handleReload = ({ preserveRecoveryMarker = false } = {}) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!preserveRecoveryMarker) {
      clearChunkRecoveryMarker();
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('_reload', Date.now().toString());
    window.location.replace(nextUrl.toString());
  };

  render() {
    if (this.state.error) {
      const isChunkLoadError = isLikelyChunkLoadError(this.state.error);
      const chunkLoadOffline = isChunkLoadError && this.state.isOffline;
      const isAutoRecovering =
        isChunkLoadError && this.state.isAutoRecovering && !chunkLoadOffline;
      const canRetryRender = !isChunkLoadError && !isAutoRecovering;

      let title = 'Unable to load OceanMind';
      let description =
        'The current page failed to load. Reload the app to recover and continue editing.';

      if (chunkLoadOffline) {
        title = 'Connection lost while loading OceanMind';
        description =
          'OceanMind cannot download the latest bundle while you are offline. Reconnect to the internet and retry.';
      } else if (isAutoRecovering) {
        title = 'Reconnecting to OceanMind';
        description =
          'Requesting a fresh bundle now. If recovery does not finish in a few seconds, reload manually.';
      } else if (isChunkLoadError) {
        title = 'OceanMind has been updated';
        description =
          'A newer bundle is available. Reload to open the latest version safely.';
      }

      return (
        <div
          className="route-load-error-screen"
          role="alert"
          aria-live="assertive"
          aria-busy={isAutoRecovering}
        >
          <div className="route-load-error-card">
            <h1 className="route-load-error-title">{title}</h1>
            <p className="route-load-error-description">{description}</p>
            {chunkLoadOffline ? (
              <p className="route-load-error-note">
                OceanMind will automatically retry once connection is restored.
              </p>
            ) : null}
            <div className="route-load-error-actions">
              {canRetryRender ? (
                <button
                  type="button"
                  className="route-load-error-button route-load-error-button-secondary"
                  onClick={this.handleRetryRender}
                >
                  Try rendering again
                </button>
              ) : null}
              <button
                type="button"
                className="route-load-error-button"
                onClick={this.handleReload}
                disabled={isAutoRecovering || chunkLoadOffline}
              >
                {chunkLoadOffline
                  ? 'Waiting for connection...'
                  : isAutoRecovering
                    ? 'Reconnecting...'
                    : 'Reload OceanMind'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteLoadErrorBoundary;
